import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useCookies } from "react-cookie";
import { CARD_STYLES } from "../../../constants/styles";
import ServicesAnalyticsApi, {
    ServicesAnalyticsData,
} from "../../../api/ServicesAnalytics";
import { PeriodSelection } from "./types";
import { SECTION_LABEL_SX, SortPill, RankedRow } from "./analyticsUi";

interface Props {
    selection: PeriodSelection;
}

type SortMode = "revenue" | "frequency";

const ServicesAnalytics: React.FC<Props> = ({ selection }) => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;

    const [cookies] = useCookies(["uuid", "brand_id", "location_id"]);
    const { uuid, brand_id, location_id } = cookies;

    const [data, setData] = useState<ServicesAnalyticsData | undefined>();
    const [sortMode, setSortMode] = useState<SortMode>("revenue");
    const requestIdRef = useRef(0);

    useEffect(() => {
        if (!uuid || !brand_id || !location_id) return;
        const requestId = ++requestIdRef.current;
        ServicesAnalyticsApi.info(uuid, brand_id, location_id, selection).then(
            (res) => {
                if (requestId !== requestIdRef.current) return; // stale
                setData(res);
            }
        );
        // `selection` is memoized in the parent (stable per period/from/to).
    }, [uuid, brand_id, location_id, selection]);

    const sortedServices = useMemo(() => {
        if (!data) return [];
        const copy = [...data.services];
        copy.sort((a, b) =>
            sortMode === "revenue" ? b.revenue - a.revenue : b.count - a.count
        );
        return copy;
    }, [data, sortMode]);

    if (!data) return null;

    return (
        <Stack className="force-dynamic-translation" spacing={2} width="100%">
            {/* Top services */}
            <Stack sx={CARD_STYLES} spacing={1.5}>
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    gap={0.75}
                >
                    <Typography sx={SECTION_LABEL_SX}>Top services</Typography>
                    <Stack direction="row" spacing={0.75}>
                        <SortPill
                            label="€ revenue"
                            active={sortMode === "revenue"}
                            onClick={() => setSortMode("revenue")}
                            color={primary}
                        />
                        <SortPill
                            label="frequency"
                            active={sortMode === "frequency"}
                            onClick={() => setSortMode("frequency")}
                            color={primary}
                        />
                    </Stack>
                </Stack>

                {sortedServices.length === 0 ? (
                    <Typography fontSize="13px" color="text.secondary">
                        No completed bookings in this period.
                    </Typography>
                ) : (
                    <Stack spacing={1.4} pt={0.25}>
                        {sortedServices.map((s, i) => (
                            <RankedRow
                                key={`${s.name}-${i}`}
                                index={i + 1}
                                name={s.name}
                                value={`€${Math.round(s.revenue)}`}
                                sub={`×${s.count}`}
                                color={primary}
                            />
                        ))}
                    </Stack>
                )}
            </Stack>

            {/* Dormant services */}
            {data.dormantServices.length > 0 && (
                <Stack sx={CARD_STYLES} spacing={1}>
                    <Typography sx={SECTION_LABEL_SX}>
                        Not booked in 3 months
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                        {data.dormantServices.map((s, i) => (
                            <Box
                                key={`${s.name}-${i}`}
                                sx={{
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: "10px",
                                    bgcolor: alpha(primary, 0.06),
                                }}
                            >
                                <Typography
                                    fontSize="12px"
                                    color="text.secondary"
                                >
                                    {s.name}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Stack>
            )}
        </Stack>
    );
};

export default ServicesAnalytics;
